import React, { useCallback, useEffect, useId, useState } from 'react';

type Perfil =
  | 'jornalista'
  | 'assessoria'
  | 'ong'
  | 'advogado'
  | 'cidadão'
  | 'outro';

const PERFIS: { id: Perfil; label: string }[] = [
  { id: 'jornalista', label: 'Jornalista ou mídia' },
  { id: 'assessoria', label: 'Assessoria parlamentar' },
  { id: 'ong', label: 'ONG ou ativismo' },
  { id: 'advogado', label: 'Advogado ou consultoria' },
  { id: 'cidadão', label: 'Cidadão interessado' },
  { id: 'outro', label: 'Outro' },
];

type Props = {
  open: boolean;
  origem: string;
  onClose: () => void;
};

export default function LeadCaptureModal({ open, origem, onClose }: Props) {
  const titleId = useId();
  const [step, setStep] = useState(1);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [interesse, setInteresse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailFallback, setShowEmailFallback] = useState(false);

  const reset = useCallback(() => {
    setStep(1);
    setPerfil(null);
    setNome('');
    setEmail('');
    setInteresse('');
    setSubmitting(false);
    setError(null);
    setShowEmailFallback(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, reset]);

  const canNextFrom1 = perfil !== null;
  const canSubmit =
    nome.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function submit() {
    if (!perfil || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    setShowEmailFallback(false);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nome.trim(),
          email: email.trim(),
          perfil,
          interesse: interesse.trim() || undefined,
          origem,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Não foi possível enviar.');
        return;
      }
      setStep(4);
    } catch {
      setShowEmailFallback(true);
      setError(null);
    } finally {
      setSubmitting(false);
    }
  }

  function sendEmailFallback() {
    const subject = encodeURIComponent('Solicitação de acesso - legislacao.online');
    const body = encodeURIComponent(
      `Nome: ${nome}\nE-mail: ${email}\nPerfil: ${perfil}\nInteresse: ${interesse}\n\nOrigem: ${origem}`
    );
    window.location.href = `mailto:lucashenrique125@gmail.com?subject=${subject}&body=${body}`;
  }

  if (!open) return null;

  return (
    <div
      className="lead-modal-root"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="lead-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button type="button" className="lead-modal-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>

        {step < 4 && (
          <div className="lead-modal-progress" aria-hidden>
            {[1, 2, 3].map((n) => (
              <span key={n} className={`lead-modal-dot ${step >= n ? 'on' : ''}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <>
            <h2 id={titleId} className="lead-modal-title">
              Como você usa legislação no dia a dia?
            </h2>
            <p className="lead-modal-sub">Escolha uma opção para personalizarmos seu acesso.</p>
            <div className="lead-modal-chips">
              {PERFIS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`lead-chip ${perfil === p.id ? 'selected' : ''}`}
                  onClick={() => setPerfil(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="lead-modal-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                Agora não
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!canNextFrom1}
                onClick={() => {
                  setError(null);
                  setStep(2);
                }}
              >
                Continuar
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 id={titleId} className="lead-modal-title">
              Seus dados de contato
            </h2>
            <p className="lead-modal-sub">Usamos apenas para liberar o acesso e avisar novidades.</p>
            <div className="lead-modal-fields">
              <label className="lead-field">
                <span>Nome</span>
                <input
                  type="text"
                  autoComplete="name"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Nome completo"
                />
              </label>
              <label className="lead-field">
                <span>E-mail</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com"
                />
              </label>
            </div>
            {error && <p className="lead-modal-error">{error}</p>}
            <div className="lead-modal-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setError(null);
                  setStep(1);
                }}
              >
                Voltar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!canSubmit}
                onClick={() => {
                  setError(null);
                  setStep(3);
                }}
              >
                Continuar
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 id={titleId} className="lead-modal-title">
              O que você quer acompanhar?
            </h2>
            <p className="lead-modal-sub">Opcional — ex.: um tema, cliente ou pauta.</p>
            <label className="lead-field lead-field-block">
              <span>Em uma frase</span>
              <textarea
                value={interesse}
                onChange={(e) => setInteresse(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder='Ex.: "reforma tributária e impacto em PMEs"'
              />
            </label>
            {error && <p className="lead-modal-error">{error}</p>}
            {showEmailFallback && (
              <div className="lead-modal-fallback">
                <p className="lead-modal-error">
                  Não conseguimos conectar. Envie por e-mail em vez disso:
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={sendEmailFallback}
                >
                  Enviar por e-mail
                </button>
              </div>
            )}
            <div className="lead-modal-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setError(null);
                  setShowEmailFallback(false);
                  setStep(2);
                }}
              >
                Voltar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={submitting || showEmailFallback}
                onClick={() => void submit()}
              >
                {submitting ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h2 id={titleId} className="lead-modal-title">
              Recebemos seu pedido
            </h2>
            <p className="lead-modal-sub">
              Entraremos em contato em breve com o próximo passo para acessar a plataforma.
            </p>
            <div className="lead-modal-actions">
              <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
                Fechar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
